import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api'; // Ensure BASE_URL is defined in your config/api.js
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf'; // Import jsPDF
import 'jspdf-autotable'; // Import jsPDF autotable plugin

const LakersList = () => {
    const [lakers, setLakers] = useState([]);
    const [filteredLakers, setFilteredLakers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const navigate = useNavigate();

    // Fetch Lakers data when the component mounts
    useEffect(() => {
        fetch(`${BASE_URL}lakers`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 1) {
                    setLakers(data.data);
                    setFilteredLakers(data.data); // Initially set filteredLakers to all Lakers data
                } else {
                    console.error('Error fetching lakers:', data.message);
                }
            })
            .catch(error => console.error('Error fetching lakers:', error));
    }, []);

    // Handle search term change
    const handleSearchChange = (event) => {
        const term = event.target.value;
        setSearchTerm(term);
        filterLakers(term, startDate, endDate); // Filter based on search term and date range
    };

    // Filter Lakers based on the search term, start date, and end date
    const filterLakers = (term, start, end) => {
        let filtered = lakers.filter(laker => {
            const matchesSearch = laker.customer_name.toLowerCase().includes(term.toLowerCase());

            // Convert created_at to YYYY-MM-DD format to avoid time parts interfering
            const lakerDate = new Date(laker.created_at).toISOString().split('T')[0]; // Format as YYYY-MM-DD

            const matchesDateRange = (
                (!start || new Date(lakerDate) >= new Date(start)) && 
                (!end || new Date(lakerDate) <= new Date(end))
            );

            return matchesSearch && matchesDateRange;
        });

        setFilteredLakers(filtered); // Update filtered lakers state
    };

    // Handle date range change (apply filters)
    const handleDateChange = () => {
        filterLakers(searchTerm, startDate, endDate); // Reapply filters when dates change
    };

    // Navigate to the edit laker page
    const handleEdit = (id) => {
        navigate(`/EditLaker/${id}`);
    };

    // Handle laker deletion
    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this laker?")) {
            fetch(`${BASE_URL}lakers/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(response => response.json())
                .then(data => {
                    if (data.status === 1) {
                        // Remove the deleted laker from both lists
                        setLakers(lakers.filter(laker => laker.id !== id));
                        setFilteredLakers(filteredLakers.filter(laker => laker.id !== id));
                    }
                })
                .catch(error => console.error('Error deleting laker:', error));
        }
    };

    // Calculate total PCS and total Gross Weight
    const calculateTotals = () => {
        let totalPCS = 0;
        let totalGrossWeight = 0;

        filteredLakers.forEach(laker => {
            totalPCS += laker.pcs || 0; // Add PCS
            totalGrossWeight += Number(laker.gross_weight) || 0; // Convert to number and add Gross Weight
        });

        return { totalPCS, totalGrossWeight }; // Return total PCS and Gross Weight
    };

    const { totalPCS, totalGrossWeight } = calculateTotals(); // Destructure to get total PCS and Gross Weight

    // Export to PDF function
    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Add Title
        doc.text('Lakers List', 14, 10); // Title at the top of the PDF

        // Add Total PCS and Total Gross Weight
        doc.text(`Total PCS: ${totalPCS}`, 14, 20);
        doc.text(`Total Gross Weight: ${totalGrossWeight}`, 14, 30);

        // Prepare table data for PDF
        const tableData = filteredLakers.map(laker => [
            laker.customer_name || 'N/A',
            laker.pcs || 'N/A',
            laker.gross_weight || 'N/A',
            laker.description || 'N/A',
        ]);

        // Add table to PDF with autoTable
        doc.autoTable({
            head: [['Customer Name', 'PCS', 'Gross Weight', 'Description']], // Table headers
            body: tableData, // Table body (filtered Lakers)
        });

        // Save the PDF with a filename
        doc.save('lakers_list.pdf');
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Lakers List</h3>
                        </div>
                    </div>
                </div>
            </div>

            <section className="section">
                <div className="row" id="basic-table">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-md-6">
                                        {/* Display Total PCS and Total Gross Weight */}
                                        <h5>Total PCS: {totalPCS}</h5>
                                        <h5>Total Gross Weight: {totalGrossWeight}</h5>
                                    </div>
                                    <div className="col-md-6 text-end">
                                        <button className="btn btn-primary" onClick={() => navigate('/AddLaker')}>Add Laker</button>
                                        <button className="btn btn-secondary ms-2" onClick={handleExportPDF}>Export to PDF</button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                {/* Search and Date Range Filter */}
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label htmlFor="searchTerm" className="form-label">Search by Customer Name</label>
                                        <input
                                            type="text"
                                            id="searchTerm"
                                            className="form-control"
                                            placeholder="Search by customer name"
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="startDate" className="form-label">Start Date</label>
                                        <input
                                            type="date"
                                            id="startDate"
                                            className="form-control"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="endDate" className="form-label">End Date</label>
                                        <input
                                            type="date"
                                            id="endDate"
                                            className="form-control"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <button className="btn btn-secondary" onClick={handleDateChange}>Apply Date Filter</button>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-lg">
                                        <thead>
                                            <tr>
                                                <th>Customer Name</th>
                                                <th>PCS</th>
                                                <th>Gross Weight</th>
                                                <th>Description</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredLakers.map(laker => (
                                                <tr key={laker.id}>
                                                    <td>{laker.customer_name}</td>
                                                    <td>{laker.pcs}</td>
                                                    <td>{laker.gross_weight}</td>
                                                    <td>{laker.description || 'N/A'}</td>
                                                    <td className="text-end">
                                                        <button className="btn btn-success" onClick={() => handleEdit(laker.id)}>Edit</button>
                                                        <button className="btn btn-danger ms-2" onClick={() => handleDelete(laker.id)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LakersList;
